import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, authHeader } from "../lib/api";
import { prepareRecordingBlob, recordingFileExtension } from "../lib/fixRecordingBlob";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import {
  CheckCircle2,
  Loader2,
  PartyPopper,
  AlertTriangle,
  Upload,
  MonitorOff,
} from "lucide-react";

const ThankYou = () => {
  const { token, compositeVideoBlob, setCompositeVideoBlob, session, streamRef, clearAuth } = useGame();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const aborted = params.get("aborted") === "1";

  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const started = useRef(false);

  const videoBlob = compositeVideoBlob?.size
    ? compositeVideoBlob
    : location.state?.compositeBlob?.size
      ? location.state.compositeBlob
      : null;

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (!aborted && (!token || !session?.session_id)) navigate("/");
  }, [aborted, token, session, navigate, streamRef]);

  useEffect(() => {
    if (uploaded || aborted) return;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploaded, aborted]);

  const runUpload = useCallback(async () => {
    if (aborted || uploading || uploaded || !session?.session_id) return;
    if (!videoBlob?.size) {
      setError("Vidéo manquante — contactez la production.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError("");

    const sessionId = session.session_id;

    try {
      const preparedBlob = await prepareRecordingBlob(videoBlob);
      const ext = recordingFileExtension(preparedBlob);
      const form = new FormData();
      form.append("file", preparedBlob, `session-${sessionId}-plateau.${ext}`);

      const { data } = await api.post(`/sessions/${sessionId}/video/composite`, form, {
        ...authHeader(token),
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
        },
      });
      if (!data?.ok || !data?.size) throw new Error("Réponse serveur invalide");
      setUploadProgress(100);
      setUploaded(true);
      setCompositeVideoBlob(null);
      toast.success("Enregistrement envoyé à la production");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Échec de l'envoi";
      setError(detail);
      toast.error("Échec de l'envoi de la vidéo");
    } finally {
      setUploading(false);
    }
  }, [aborted, uploading, uploaded, session, videoBlob, token, setCompositeVideoBlob]);

  useEffect(() => {
    if (started.current || aborted || !session?.session_id) return;
    if (!videoBlob?.size) return;
    started.current = true;
    void runUpload();
  }, [aborted, session?.session_id, videoBlob, runUpload]);

  if (aborted) {
    return (
      <PageLayout>
        <div className="text-center fade-up">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/30 mb-6">
            <AlertTriangle className="text-red-400" size={40} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Session interrompue</h2>
          <p className="text-white/60 leading-relaxed max-w-lg mx-auto">
            Vous ne pouvez pas poursuivre : vous avez déjà lancé une session de jeu. Si vous avez
            souhaité refaire le parcours depuis le début en rafraîchissant la page, cela n&apos;est
            pas autorisé. Pour des raisons d&apos;équité, votre code d&apos;accès a été consommé et
            ne peut plus être réutilisé.
          </p>
          <p className="mt-3 text-sm text-white/40">
            Si vous pensez qu&apos;il s&apos;agit d&apos;une erreur, contactez la production.
          </p>
        </div>
        <div className="max-w-xs mx-auto">
          <button
            type="button"
            onClick={() => {
              clearAuth();
              navigate("/");
            }}
            className="md-btn-secondary w-full py-3 mt-10"
            data-testid="home-button"
          >
            Retour à l&apos;accueil
          </button>
        </div>
      </PageLayout>
    );
  }

  if (!uploaded) {
    const statusLabel = error
      ? "Échec de l'envoi"
      : uploading
        ? `Envoi sécurisé en cours (${uploadProgress} %)…`
        : videoBlob?.size
          ? "Préparation de l'envoi…"
          : "Finalisation de l'enregistrement…";

    return (
      <PageLayout>
        <div className="max-w-lg mx-auto fade-up">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-500/10 border-2 border-amber-400/40 mb-6">
              {error ? (
                <Upload className="text-amber-300" size={40} />
              ) : (
                <Loader2 className="text-amber-300 animate-spin" size={40} />
              )}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {error ? "Envoi interrompu" : "Envoi de votre vidéo en cours"}
            </h2>
            <p className="text-white/60 leading-relaxed">
              Votre partie est terminée. Nous préparons et transmettons votre enregistrement au studio.
            </p>
          </div>

          <div
            className="mt-8 p-5 rounded-2xl border-2 border-amber-400/50 bg-amber-500/10 text-left"
            role="alert"
            data-testid="upload-wait-notice"
          >
            <div className="flex items-start gap-3">
              <MonitorOff className="text-amber-300 shrink-0 mt-0.5" size={22} />
              <div>
                <p className="font-bold text-amber-100 text-lg leading-snug">
                  Merci de patienter — ne fermez pas cette page
                </p>
                <p className="mt-2 text-sm text-amber-100/85 leading-relaxed">
                  Ne fermez pas l&apos;onglet ni la fenêtre tant que l&apos;envoi n&apos;est pas terminé. La
                  fermeture pourrait empêcher la transmission de votre vidéo au studio.
                </p>
              </div>
            </div>
          </div>

          <div className="md-card p-5 mt-6 text-left">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.3em] text-white/45">Statut vidéo</div>
              {uploading && !error && <Loader2 className="animate-spin text-md-cyan" size={18} />}
              {error && <AlertTriangle className="text-red-400" size={18} />}
            </div>
            <div className="mt-2 font-semibold text-lg text-white" data-testid="upload-status">
              {error || statusLabel}
            </div>

            {uploading && !error && (
              <div className="mt-4">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-md-cyan transition-[width] duration-300 ease-out"
                    style={{ width: `${uploadProgress || 8}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4">
                <p className="text-sm text-red-400">{error}</p>
                <button
                  type="button"
                  onClick={() => {
                    started.current = false;
                    setError("");
                    void runUpload();
                  }}
                  disabled={uploading || !videoBlob?.size}
                  className="md-btn-solid w-full py-3 mt-4 disabled:opacity-50"
                  data-testid="retry-upload-button"
                >
                  Réessayer l&apos;envoi
                </button>
              </div>
            )}
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="text-center fade-up">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-md-electric/10 border-2 border-md-cyan/30 mb-6">
          <PartyPopper className="text-md-cyan" size={40} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Merci pour votre passage</h2>
        <p className="text-white/60 leading-relaxed max-w-lg mx-auto">
          Votre candidature au casting du Jeu Le Maillon Faible a bien été enregistrée. La production vous
          recontactera prochainement.
        </p>

        <div className="md-card p-5 mt-8 text-left max-w-md mx-auto border-emerald-500/25 bg-emerald-500/5">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.3em] text-white/45">Statut vidéo</div>
            <CheckCircle2 className="text-emerald-400" size={18} />
          </div>
          <div className="mt-2 font-semibold text-lg text-emerald-300" data-testid="upload-status">
            Vidéo transmise avec succès
          </div>
          <p className="mt-2 text-sm text-white/50">
            Vous pouvez maintenant quitter cette page en toute sécurité.
          </p>
        </div>
      </div>

      <div className="max-w-xs mx-auto">
        <MdGlowButton
          onClick={() => {
            clearAuth();
            navigate("/");
          }}
          wrapClassName="mt-10"
          data-testid="home-button"
        >
          Retour à l&apos;accueil
        </MdGlowButton>
      </div>
    </PageLayout>
  );
};

export default ThankYou;
